import React, { useState, useEffect, useRef, useContext } from 'react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Fab from '@mui/material/Fab';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';

import { AuthContext } from '../contexts/AuthContext';
import { GoveeUIClient } from '../goveepb/internal/proto/govee_grpc_web_pb';
import { ChatRequest, ChatMessage } from '../goveepb/internal/proto/govee_pb';

const DRAWER_WIDTH = 'clamp(380px, 35vw, 600px)';

const client = new GoveeUIClient(process.env.REACT_APP_GRPC_URL);

function formatUsage(usage, latencyMs) {
  if (!usage || (usage.inputTokens === 0 && usage.outputTokens === 0)) return null;
  const inK = (usage.inputTokens / 1000).toFixed(1);
  const outK = (usage.outputTokens / 1000).toFixed(1);
  const cost = usage.costUsd.toFixed(3);
  const parts = [`${inK}k in / ${outK}k out · $${cost}`];
  if (latencyMs != null) parts.push(`${(latencyMs / 1000).toFixed(1)}s`);
  return parts.join(' · ');
}

function UsageRow({ usage, latencyMs }) {
  const label = formatUsage(usage, latencyMs);
  if (!label) return null;
  return (
    <Typography
      variant="caption"
      sx={{ display: 'block', color: 'text.disabled', fontSize: '0.68rem', mt: 0.5, lineHeight: 1.3 }}
    >
      {label}
    </Typography>
  );
}

function MessageBubble({ message }) {
  const isUser = message.role === 'user';
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        mb: 1,
      }}
    >
      <Box sx={{ maxWidth: '80%' }}>
        <Box
          sx={{
            px: 1.5,
            py: 1,
            borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
            bgcolor: isUser ? 'primary.main' : 'grey.200',
            color: isUser ? 'primary.contrastText' : 'text.primary',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>{message.content}</Typography>
        </Box>
        {!isUser && <UsageRow usage={message.usage} latencyMs={message.latencyMs} />}
      </Box>
    </Box>
  );
}

export default function SupportChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [progressMessage, setProgressMessage] = useState('');
  const [pendingUsage, setPendingUsage] = useState(null);
  const requestStartTimeRef = useRef(null);
  const messagesEndRef = useRef(null);
  const { user, getAccessToken, refreshToken } = useContext(AuthContext);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading, progressMessage]);

  const buildRequest = (history) => {
    const request = new ChatRequest();
    const historyList = history.map((msg) => {
      const chatMsg = new ChatMessage();
      chatMsg.setRole(msg.role);
      chatMsg.setContent(msg.content);
      return chatMsg;
    });
    request.setHistoryList(historyList);
    return request;
  };

  const extractUsage = (response) => {
    const u = response.getUsage();
    if (!u) return null;
    return { inputTokens: u.getInputTokens(), outputTokens: u.getOutputTokens(), costUsd: u.getCostUsd() };
  };

  const openStream = (request, token, isRetry) => {
    const metadata = { Authorization: 'Bearer ' + token };
    const stream = client.chat(request, metadata);

    stream.on('data', (response) => {
      const progress = response.getProgressMessage();
      if (progress) {
        setProgressMessage(progress);
        const usage = extractUsage(response);
        if (usage) setPendingUsage(usage);
        return;
      }
      const content = response.getContent();
      if (content) {
        const latencyMs = requestStartTimeRef.current != null
          ? Date.now() - requestStartTimeRef.current
          : null;
        const usage = extractUsage(response);
        setProgressMessage('');
        setPendingUsage(null);
        requestStartTimeRef.current = null;
        setLoading(false);
        setMessages((prev) => [...prev, { role: 'assistant', content, usage, latencyMs }]);
      }
    });

    stream.on('error', (err) => {
      if (err.code === 16 /* UNAUTHENTICATED */ && !isRetry) {
        refreshToken().then(() => {
          openStream(request, getAccessToken(), true);
        }).catch(() => {
          setProgressMessage('');
          setPendingUsage(null);
          requestStartTimeRef.current = null;
          setLoading(false);
          setMessages((prev) => [
            ...prev,
            { role: 'assistant', content: 'Session expired. Please refresh the page and try again.' },
          ]);
        });
        return;
      }
      setProgressMessage('');
      setPendingUsage(null);
      requestStartTimeRef.current = null;
      setLoading(false);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Sorry, something went wrong. Please try again.' },
      ]);
    });
  };

  const sendMessage = () => {
    const text = inputValue.trim();
    if (!text || loading) return;

    const userMessage = { role: 'user', content: text };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInputValue('');
    setLoading(true);
    setPendingUsage(null);
    requestStartTimeRef.current = Date.now();
    setProgressMessage('Thinking…');

    const request = buildRequest(updatedMessages);
    openStream(request, getAccessToken(), false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!user) return null;

  return (
    <>
      <Fab
        color="primary"
        size="medium"
        onClick={() => setOpen(true)}
        sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1200 }}
        aria-label="Open support chat"
      >
        <HelpOutlineIcon />
      </Fab>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{ sx: { width: DRAWER_WIDTH, display: 'flex', flexDirection: 'column' } }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            borderBottom: 1,
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold">
            AI Agent Support
          </Typography>
          <IconButton size="small" onClick={() => setOpen(false)} aria-label="Close support chat">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Message list */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1.5 }}>
          {messages.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
              Ask a question about your Govee devices.
            </Typography>
          )}
          {messages.map((msg, idx) => (
            <MessageBubble key={idx} message={msg} />
          ))}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
              <Box sx={{ maxWidth: '80%' }}>
                <Box
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderRadius: '16px 16px 16px 4px',
                    bgcolor: 'grey.200',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <CircularProgress size={14} thickness={5} />
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.82rem' }}>
                    {progressMessage || 'Thinking…'}
                  </Typography>
                </Box>
                <UsageRow usage={pendingUsage} />
              </Box>
            </Box>
          )}
          <div ref={messagesEndRef} />
        </Box>

        {/* Input bar */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            gap: 1,
            alignItems: 'flex-end',
            flexShrink: 0,
          }}
        >
          <TextField
            fullWidth
            multiline
            maxRows={4}
            size="small"
            placeholder="Ask about your devices…"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading}
          />
          <IconButton
            color="primary"
            onClick={sendMessage}
            disabled={!inputValue.trim() || loading}
            aria-label="Send message"
          >
            <SendIcon />
          </IconButton>
        </Box>
      </Drawer>
    </>
  );
}
